import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Clock, Globe, Loader2, Mail, Phone } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { BUDGET_OPTIONS, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL, MEETING_FORMATS, SERVICE_OPTIONS } from "@/data/content";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const API_URL = `${API_BASE}/api/kontakt`;

const initialForm = {
  foretagsnamn: "",
  namn: "",
  epost: "",
  telefon: "",
  hemsida: "",
  hjalp_med: "",
  beskrivning: "",
  budget: "",
  motesformat: "",
  onskat_datum: "",
  meddelande: "",
  fax: "",
};

const inputClass = (hasError) =>
  `w-full rounded-lg border bg-navy px-4 py-3 text-sm text-cream placeholder:text-cream/30 [color-scheme:dark] transition-[border-color,box-shadow] focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-400/60 focus:border-red-400 focus:ring-red-400/20"
      : "border-white/15 focus:border-orange focus:ring-orange/20"
  }`;

const Field = ({ id, label, required = false, error, children }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block font-display text-sm font-semibold text-cream">
      {label} {required && <span aria-hidden="true" className="text-orange">*</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm font-medium text-red-300">
        {error}
      </p>
    )}
  </div>
);

const ContactForm = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.foretagsnamn.trim().length < 2) e.foretagsnamn = "Ange företagets namn.";
    if (form.namn.trim().length < 2) e.namn = "Ange ditt namn.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.epost.trim())) e.epost = "Ange en giltig e-postadress.";
    if (form.telefon.replace(/[^0-9+]/g, "").length < 6) e.telefon = "Ange ett giltigt telefonnummer.";
    if (!form.hjalp_med) e.hjalp_med = "Välj vad du behöver hjälp med.";
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      document.querySelector("[data-invalid='true']")?.focus();
      return;
    }
    setStatus("sending");
    setServerError("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setServerError(
          typeof data.detail === "string"
            ? data.detail
            : "Något gick fel. Försök igen eller maila oss på info@tillvia.se."
        );
      }
    } catch {
      setStatus("error");
      setServerError("Kunde inte nå servern. Kontrollera din internetanslutning och försök igen.");
    }
  };

  const reset = () => {
    setForm(initialForm);
    setErrors({});
    setStatus("idle");
    setServerError("");
  };

  return (
    <section id="kontakt" className="bg-navy py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <Eyebrow>Kontakt</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Boka kostnadsfritt möte
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-cream/60">
            Berätta vad du behöver hjälp med så återkommer vi med ett förslag på upplägg. Mötet är kostnadsfritt och
            utan förpliktelser.
          </p>
          <div className="mt-8 space-y-3 rounded-2xl border border-white/10 bg-navy2 p-6">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-email-link"
              className="flex items-center gap-3 text-sm font-medium text-cream/85 transition-colors hover:text-orange"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal text-cream">
                <Mail size={17} aria-hidden="true" />
              </span>
              {CONTACT_EMAIL}
            </a>
            <a
              href={`tel:${CONTACT_PHONE_TEL}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-3 text-sm font-medium text-cream/85 transition-colors hover:text-orange"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal text-cream">
                <Phone size={17} aria-hidden="true" />
              </span>
              {CONTACT_PHONE}
            </a>
            <p className="flex items-center gap-3 text-sm font-medium text-cream/85">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal text-cream">
                <Globe size={17} aria-hidden="true" />
              </span>
              tillvia.se
            </p>
            <p className="flex items-center gap-3 text-sm font-medium text-cream/85">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal text-cream">
                <Clock size={17} aria-hidden="true" />
              </span>
              Vi svarar oftast inom 24 timmar
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          <div className="rounded-2xl border border-white/10 bg-navy2 p-6 shadow-2xl shadow-black/30 sm:p-8">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="tack"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid="form-success-message"
                  className="py-10 text-center"
                >
                  <CheckCircle2 size={56} className="mx-auto text-teal" aria-hidden="true" />
                  <h3 className="mt-5 font-display text-2xl font-bold text-white">Tack för din förfrågan!</h3>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cream/60 sm:text-base">
                    Vi har tagit emot ditt meddelande och återkommer till dig så snart vi kan – oftast inom ett dygn.
                  </p>
                  <button
                    type="button"
                    data-testid="form-reset-button"
                    onClick={reset}
                    className="mt-7 inline-flex items-center justify-center rounded-lg bg-teal px-6 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-slate2"
                  >
                    Skicka en till förfrågan
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                  onSubmit={onSubmit}
                  noValidate
                  data-testid="booking-form"
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field id="foretagsnamn" label="Företagsnamn" required error={errors.foretagsnamn}>
                      <input
                        id="foretagsnamn"
                        name="foretagsnamn"
                        type="text"
                        autoComplete="organization"
                        value={form.foretagsnamn}
                        onChange={handleChange}
                        aria-invalid={!!errors.foretagsnamn}
                        data-invalid={!!errors.foretagsnamn || undefined}
                        data-testid="form-field-company"
                        className={inputClass(!!errors.foretagsnamn)}
                      />
                    </Field>
                    <Field id="namn" label="Ditt namn" required error={errors.namn}>
                      <input
                        id="namn"
                        name="namn"
                        type="text"
                        autoComplete="name"
                        value={form.namn}
                        onChange={handleChange}
                        aria-invalid={!!errors.namn}
                        data-invalid={!!errors.namn || undefined}
                        data-testid="form-field-name"
                        className={inputClass(!!errors.namn)}
                      />
                    </Field>
                    <Field id="epost" label="E-post" required error={errors.epost}>
                      <input
                        id="epost"
                        name="epost"
                        type="email"
                        autoComplete="email"
                        value={form.epost}
                        onChange={handleChange}
                        aria-invalid={!!errors.epost}
                        data-invalid={!!errors.epost || undefined}
                        data-testid="form-field-email"
                        className={inputClass(!!errors.epost)}
                      />
                    </Field>
                    <Field id="telefon" label="Telefonnummer" required error={errors.telefon}>
                      <input
                        id="telefon"
                        name="telefon"
                        type="tel"
                        autoComplete="tel"
                        value={form.telefon}
                        onChange={handleChange}
                        aria-invalid={!!errors.telefon}
                        data-invalid={!!errors.telefon || undefined}
                        data-testid="form-field-phone"
                        className={inputClass(!!errors.telefon)}
                      />
                    </Field>
                    <Field id="hemsida" label="Företagets hemsida (valfritt)" error={errors.hemsida}>
                      <input
                        id="hemsida"
                        name="hemsida"
                        type="text"
                        inputMode="url"
                        autoComplete="url"
                        placeholder="t.ex. dittforetag.se"
                        value={form.hemsida}
                        onChange={handleChange}
                        data-testid="form-field-website"
                        className={inputClass(false)}
                      />
                    </Field>
                    <Field id="hjalp_med" label="Vad behöver du hjälp med?" required error={errors.hjalp_med}>
                      <select
                        id="hjalp_med"
                        name="hjalp_med"
                        value={form.hjalp_med}
                        onChange={handleChange}
                        aria-invalid={!!errors.hjalp_med}
                        data-invalid={!!errors.hjalp_med || undefined}
                        data-testid="form-field-service"
                        className={inputClass(!!errors.hjalp_med)}
                      >
                        <option value="">Välj ett alternativ…</option>
                        {SERVICE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <div className="sm:col-span-2">
                      <Field id="beskrivning" label="Beskriv vad du behöver hjälp med" error={errors.beskrivning}>
                        <textarea
                          id="beskrivning"
                          name="beskrivning"
                          rows={4}
                          value={form.beskrivning}
                          onChange={handleChange}
                          data-testid="form-field-description"
                          className={`${inputClass(false)} resize-y`}
                        />
                      </Field>
                    </div>
                    <Field id="budget" label="Ungefärlig budget" error={errors.budget}>
                      <select
                        id="budget"
                        name="budget"
                        value={form.budget}
                        onChange={handleChange}
                        data-testid="form-field-budget"
                        className={inputClass(false)}
                      >
                        <option value="">Välj ett alternativ…</option>
                        {BUDGET_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field id="onskat_datum" label="Önskat datum" error={errors.onskat_datum}>
                      <input
                        id="onskat_datum"
                        name="onskat_datum"
                        type="date"
                        value={form.onskat_datum}
                        onChange={handleChange}
                        data-testid="form-field-date"
                        className={inputClass(false)}
                      />
                    </Field>
                    <fieldset className="sm:col-span-2">
                      <legend className="mb-1.5 font-display text-sm font-semibold text-cream">Mötesformat</legend>
                      <div className="flex flex-wrap gap-2">
                        {MEETING_FORMATS.map((m) => (
                          <label
                            key={m}
                            className={`cursor-pointer rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                              form.motesformat === m
                                ? "border-teal bg-teal text-white"
                                : "border-white/15 bg-navy text-cream/70 hover:border-teal/70"
                            }`}
                          >
                            <input
                              type="radio"
                              name="motesformat"
                              value={m}
                              checked={form.motesformat === m}
                              onChange={handleChange}
                              className="sr-only"
                            />
                            {m}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <div className="sm:col-span-2">
                      <Field id="meddelande" label="Övrigt meddelande" error={errors.meddelande}>
                        <textarea
                          id="meddelande"
                          name="meddelande"
                          rows={3}
                          value={form.meddelande}
                          onChange={handleChange}
                          data-testid="form-field-message"
                          className={`${inputClass(false)} resize-y`}
                        />
                      </Field>
                    </div>
                  </div>

                  <div aria-hidden="true" className="hidden">
                    <label htmlFor="fax">Faxnummer</label>
                    <input
                      id="fax"
                      name="fax"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.fax}
                      onChange={handleChange}
                    />
                  </div>

                  {status === "error" && (
                    <p
                      role="alert"
                      data-testid="form-error-message"
                      className="mt-5 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-300"
                    >
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="form-submit-button"
                    disabled={status === "sending"}
                    className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-orange px-7 py-4 font-display text-base font-bold text-navy shadow-[0_4px_14px_rgba(255,125,0,0.25)] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-orange2 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 sm:w-auto"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 size={18} className="animate-spin" aria-hidden="true" /> Skickar…
                      </>
                    ) : (
                      <>
                        Boka kostnadsfritt möte <ArrowRight size={18} aria-hidden="true" />
                      </>
                    )}
                  </button>
                  <p className="mt-4 text-xs leading-relaxed text-cream/40">
                    Genom att skicka formuläret godkänner du att Tillvia kontaktar dig angående din förfrågan.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
